/**
 * Hook for generating dynamic chart color palettes based on workspace theme
 * 
 * This hook:
 * - Derives a harmonious color palette from the workspace accent color
 * - Provides multiple palette variants (default, pastel, vibrant)
 * - Supports gradient generation for chart backgrounds
 * - Automatically updates when the theme changes
 */

import { useMemo } from "react";
import { useWorkspaceTheme } from "@/hooks/use-workspace-theme";
import { hexToHsl, hslToHex } from "@/utils/color";

// ============================================================================
// Types
// ============================================================================

export interface ChartColorPalette {
  /** Primary colors for chart series (8 colors) */
  primary: string[];
  /** Pastel/muted version of the palette */
  pastel: string[];
  /** Vibrant/saturated version of the palette */
  vibrant: string[];
  /** Single accent color for highlights */
  accent: string;
  /** Light accent for backgrounds */
  accentLight: string;
  /** Dark accent for text/borders */
  accentDark: string;
  /** Gradient stops for chart backgrounds */
  gradient: {
    start: string;
    middle: string | null;
    end: string;
  };
  /** CSS gradient string */
  gradientCSS: string;
  /** Get color by index (wraps around if index > palette length) */
  getColor: (index: number) => string;
  /** Get pastel color by index */
  getPastelColor: (index: number) => string;
  /** Get vibrant color by index */
  getVibrantColor: (index: number) => string;
}

// ============================================================================
// Color Generation Utilities
// ============================================================================

/**
 * Generate a harmonious color palette from a base color
 * Uses color theory to create visually pleasing combinations
 */
function generateHarmoniousPalette(
  baseHex: string,
  additionalColors: string[] = []
): string[] {
  const { h, s, l } = hexToHsl(baseHex);
  const palette: string[] = [baseHex];

  // Add any additional colors from the theme
  additionalColors.forEach(color => {
    if (!palette.includes(color)) {
      palette.push(color);
    }
  });

  // Generate harmonious colors using color theory
  // Triadic: +120°, +240°
  // Split-complementary: +150°, +210°
  // Analogous: +30°, -30°
  const hueShifts = [30, -30, 60, -60, 120, -120, 180];
  
  let i = 0;
  while (palette.length < 8 && i < hueShifts.length) {
    const newHue = (h + hueShifts[i] + 360) % 360;
    // Vary saturation and lightness slightly for visual interest
    const newS = Math.max(30, Math.min(90, s + (i % 2 === 0 ? 5 : -5)));
    const newL = Math.max(35, Math.min(65, l + (i % 3 === 0 ? 5 : -3)));
    const newColor = hslToHex(newHue, newS, newL);
    
    if (!palette.includes(newColor)) {
      palette.push(newColor);
    }
    i++;
  }

  // Fill remaining slots with variations if needed
  while (palette.length < 8) {
    const baseIndex = palette.length % 3;
    const { h: baseH, s: baseS, l: baseL } = hexToHsl(palette[baseIndex]);
    const variation = hslToHex(
      (baseH + 15 * palette.length) % 360,
      Math.max(30, baseS - 10),
      Math.min(70, baseL + 10)
    );
    palette.push(variation);
  }

  return palette.slice(0, 8);
}

/**
 * Create a pastel version of a color palette
 */
function createPastelPalette(colors: string[]): string[] {
  return colors.map(hex => {
    const { h, s } = hexToHsl(hex);
    // Lower saturation, higher lightness for pastel effect
    return hslToHex(h, Math.max(20, s * 0.5), 85);
  });
}

/**
 * Create a vibrant version of a color palette
 */
function createVibrantPalette(colors: string[]): string[] {
  return colors.map(hex => {
    const { h, s, l } = hexToHsl(hex);
    // Higher saturation, moderate lightness for vibrant effect
    return hslToHex(h, Math.min(100, s * 1.3), Math.max(40, Math.min(60, l)));
  });
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to get dynamic chart colors based on workspace theme
 */
export function useChartColors(): ChartColorPalette {
  const { derivedColors, allColors } = useWorkspaceTheme();

  const palette = useMemo(() => {
    // Get additional colors from theme (if multi-color gradient)
    const additionalColors = allColors.slice(1).map(c => c.color);
    
    // Generate the primary palette
    const primary = generateHarmoniousPalette(
      derivedColors.accent,
      additionalColors
    );
    
    // Create variant palettes
    const pastel = createPastelPalette(primary);
    const vibrant = createVibrantPalette(primary);

    // Gradient configuration
    const gradientColors = derivedColors.gradientColors;
    const gradient = {
      start: gradientColors[0],
      middle: gradientColors.length === 3 ? gradientColors[1] : null,
      end: gradientColors[gradientColors.length - 1],
    };

    // Generate CSS gradient string
    const gradientCSS = gradient.middle
      ? `linear-gradient(135deg, ${gradient.start} 0%, ${gradient.middle} 50%, ${gradient.end} 100%)`
      : gradientColors.length > 1
        ? `linear-gradient(135deg, ${gradient.start} 0%, ${gradient.end} 100%)`
        : gradient.start;

    return {
      primary,
      pastel,
      vibrant,
      accent: derivedColors.accent,
      accentLight: derivedColors.accentLight,
      accentDark: derivedColors.accentDark,
      gradient,
      gradientCSS,
      getColor: (index: number) => primary[index % primary.length],
      getPastelColor: (index: number) => pastel[index % pastel.length],
      getVibrantColor: (index: number) => vibrant[index % vibrant.length],
    };
  }, [derivedColors, allColors]);

  return palette;
}

/**
 * Get chart colors as CSS variables (for use in CSS-in-JS or inline styles)
 */
export function useChartColorVars(): Record<string, string> {
  const palette = useChartColors();

  return useMemo(() => {
    const vars: Record<string, string> = {
      "--chart-accent": palette.accent,
      "--chart-accent-light": palette.accentLight,
      "--chart-accent-dark": palette.accentDark,
      "--chart-gradient": palette.gradientCSS,
    };

    palette.primary.forEach((color, i) => {
      vars[`--chart-${i + 1}`] = color;
    });

    palette.pastel.forEach((color, i) => {
      vars[`--chart-pastel-${i + 1}`] = color;
    });

    palette.vibrant.forEach((color, i) => {
      vars[`--chart-vibrant-${i + 1}`] = color;
    });

    return vars;
  }, [palette]);
}

export default useChartColors;



